import {useCallback, useEffect, useRef, useState} from 'react';
import {ApiError} from '../api';
import type {Booking} from '../api/types';
import {useApi} from '../session';

export type ManagedBookingStatus = 'loading' | 'ready' | 'notFound' | 'error';

export interface ManagedBooking {
  status: ManagedBookingStatus;
  booking: Booking | null;
  error: ApiError | null;
  reload: () => void;
  /** Replace the held booking after a mutation (e.g. a cancel returned the new state). */
  set: (booking: Booking) => void;
}

/**
 * Read one booking by its `manage_token`, for BookDone and BookManage.
 *
 * A missing booking is `notFound`, not `error`: a link that no longer resolves —
 * the token was mistyped, or the booking was purged — is a dead end with its own
 * honest copy ("we can't find this booking"), the same distinction the QR flow
 * draws between an expired session and a dropped network. A network failure stays
 * `error` with a retry, because the booking may well be fine.
 *
 * `seed` renders the stored snapshot immediately while the read confirms it — a
 * guest who just created a booking should see it without a spinner, and a read
 * over 4G should not blank a screen that already has the answer.
 */
export function useManagedBooking(
  token: string | null,
  tenantId: string | null,
  seed: Booking | null = null,
): ManagedBooking {
  const client = useApi();
  const [booking, setBooking] = useState<Booking | null>(seed);
  const [status, setStatus] = useState<ManagedBookingStatus>(seed ? 'ready' : 'loading');
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const seededOnce = useRef(false);

  // Adopt the seed on first sight, so a booking created a moment ago shows at once.
  if (seed && !seededOnce.current) {
    seededOnce.current = true;
    if (!booking) setBooking(seed);
  }

  useEffect(() => {
    if (!token || !tenantId) {
      setStatus('notFound');
      return;
    }
    const controller = new AbortController();
    let cancelled = false;
    if (!booking) setStatus('loading');
    void (async () => {
      try {
        const fresh = await client.getPublicBooking(token, tenantId, controller.signal);
        if (cancelled) return;
        setBooking(fresh);
        setStatus('ready');
        setError(null);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        if (caught instanceof ApiError && (caught.status === 404 || caught.code === 'not_found')) {
          setStatus('notFound');
          setError(caught);
          return;
        }
        // A network wobble should not blank a screen that already has the seed.
        setError(caught instanceof ApiError ? caught : ApiError.network(caught));
        setStatus(booking ? 'ready' : 'error');
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // `booking` is intentionally not a dependency: it is the seed we render
    // through, not a trigger to refetch. `nonce` drives an explicit reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, token, tenantId, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback((next: Booking) => setBooking(next), []);

  return {status, booking, error, reload, set};
}
